import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Autocomplete,
} from "@mui/material";
import { AddCircleOutlineOutlined, DeleteOutline } from "@mui/icons-material";
import CustomTextField from "../../../components/common/Textfield";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";
import {
  getCache,
  setCache,
  invalidateCache,
  notifyAssignmentsUpdated,
  type ComponentManager,
} from "./AssignmentsCache";

interface Component {
  id: number;
  name: string;
  description?: string;
}

interface User {
  id: number;
  username: string;
  first_name?: string;
  last_name?: string;
}

interface FormData {
  description: string;
  version: string;
  lead_developer_id: string;
  backup_developer_id: string;
  whatsapp_group_url: string;
  figma_url?: string;
}

const emptyForm: FormData = {
  description: "",
  version: "",
  lead_developer_id: "",
  backup_developer_id: "",
  whatsapp_group_url: "",
  figma_url: "",
};

const UpdateFooter = () => {
  const [selectedComponent, setSelectedComponent] = useState("");
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [checkingAssignment, setCheckingAssignment] = useState(false);

  // Existing assignment for selected component (null = none found)
  const [existingAssignment, setExistingAssignment] =
    useState<ComponentManager | null>(null);

  // Components
  const [components, setComponents] = useState<Component[]>([]);
  const [loadingComponents, setLoadingComponents] = useState(false);

  // Users
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Add component dialog
  const [openAddComponentDialog, setOpenAddComponentDialog] = useState(false);
  const [newComponentData, setNewComponentData] = useState({
    name: "",
    description: "",
  });
  const [savingNewComponent, setSavingNewComponent] = useState(false);

  const location = useLocation();

  useEffect(() => {
    fetchComponents();
    fetchUsers();
  }, []);

  // Pre-select component passed via navigation state from Footer
  useEffect(() => {
    const state = location.state as {
      componentId?: number;
      componentName?: string;
    } | null;
    if (state?.componentId && components.length > 0) {
      setSelectedComponent(state.componentId.toString());
      // Clear state so re-visiting the page doesn't re-trigger
      window.history.replaceState({}, "");
    }
  }, [location.state, components]);

  const fetchAllAssignments = async (): Promise<ComponentManager[]> => {
    const cached = getCache();
    if (cached) return cached;

    let all: ComponentManager[] = [];
    let url: string | null = "/api/configurations/components-managers/";

    while (url) {
      const response: {
        data:
          | { results?: ComponentManager[]; next?: string | null }
          | ComponentManager[];
      } = await apiService.get(url);

      if (Array.isArray(response.data)) {
        all = response.data;
        break;
      } else {
        const paginated = response.data as {
          results?: ComponentManager[];
          next?: string | null;
        };
        all = [...all, ...(paginated.results || [])];
        const next = paginated.next ?? null;
        if (next) {
          const urlObj = new URL(next);
          url = urlObj.pathname + urlObj.search;
        } else {
          url = null;
        }
      }
    }

    setCache(all);
    return all;
  };

  const checkExistingAssignment = useCallback(async (componentId: string) => {
    setCheckingAssignment(true);
    setExistingAssignment(null);
    setFormData(emptyForm);
    try {
      const allData = await fetchAllAssignments();

      // Filter client-side since the backend ignores the component_id query param
      const match = allData.find(
        (m) => m.component.id.toString() === componentId,
      );

      if (match) {
        setExistingAssignment(match);
        setFormData({
          description: match.description || "",
          version: match.version || "",
          lead_developer_id: match.lead_developer?.id.toString() || "",
          backup_developer_id: match.backup_developer?.id.toString() || "",
          whatsapp_group_url: match.whatsapp_group_url || "",
          figma_url: match.figma_url || "",
        });
      }
    } catch (error) {
      console.error("Failed to check existing assignment:", error);
    } finally {
      setCheckingAssignment(false);
    }
  }, []);

  // When component selection changes, check for existing assignment
  useEffect(() => {
    if (!selectedComponent) {
      setExistingAssignment(null);
      setFormData(emptyForm);
      return;
    }
    checkExistingAssignment(selectedComponent);
  }, [selectedComponent, checkExistingAssignment]);

  const fetchComponents = async () => {
    setLoadingComponents(true);
    try {
      let allComponents: Component[] = [];
      let url: string | null = "/api/endpoint-request/components/";

      while (url) {
        const response: {
          data: { results?: Component[]; next?: string | null } | Component[];
        } = await apiService.get(url);

        if (Array.isArray(response.data)) {
          allComponents = response.data;
          break;
        } else {
          const paginated = response.data as {
            results?: Component[];
            next?: string | null;
          };
          allComponents = [...allComponents, ...(paginated.results || [])];
          const next: string | null = paginated.next ?? null;
          if (next) {
            const urlObj = new URL(next);
            url = urlObj.pathname + urlObj.search;
          } else {
            url = null;
          }
        }
      }

      setComponents(allComponents);
    } catch (error) {
      console.error("Failed to fetch components:", error);
      toast.error("Failed to load components.");
    } finally {
      setLoadingComponents(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      let allUsers: User[] = [];
      let url: string | null = "/api/authentication/users/";

      while (url) {
        const response: {
          data: { results?: User[]; next?: string | null } | User[];
        } = await apiService.get(url);

        if (Array.isArray(response.data)) {
          allUsers = response.data;
          break;
        } else {
          const paginated = response.data as {
            results?: User[];
            next?: string | null;
          };
          allUsers = [...allUsers, ...(paginated.results || [])];
          const next: string | null = paginated.next ?? null;
          if (next) {
            const urlObj: URL = new URL(next);
            url = urlObj.pathname + urlObj.search;
          } else {
            url = null;
          }
        }
      }

      setUsers(allUsers);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users.");
    } finally {
      setLoadingUsers(false);
    }
  };

  const getUserLabel = (user: User) => {
    const fullName = [user.first_name, user.last_name]
      .filter(Boolean)
      .join(" ");
    return fullName || user.username;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedComponent || !formData.lead_developer_id) {
      toast.error("Please fill in all required fields");
      return;
    }

    const payload = {
      component_id: Number(selectedComponent),
      description: formData.description,
      version: formData.version,
      lead_developer_id: Number(formData.lead_developer_id),
      whatsapp_group_url: formData.whatsapp_group_url || null,
      ...(formData.backup_developer_id && {
        backup_developer_id: Number(formData.backup_developer_id),
      }),
      ...(formData.figma_url && {
        figma_url: formData.figma_url,
      }),
    };

    setSubmitting(true);
    try {
      if (existingAssignment) {
        // PATCH existing assignment
        await apiService.patch(
          `/api/configurations/components-managers/${existingAssignment.id}/`,
          payload,
        );
        toast.success("Assignment updated successfully!");
      } else {
        // POST new assignment
        await apiService.post(
          "/api/configurations/components-managers/",
          payload,
        );
        toast.success("Component assigned successfully!");
      }

      invalidateCache();
      notifyAssignmentsUpdated();
      setOpenModal(true);
      setSelectedComponent("");
      setFormData(emptyForm);
      setExistingAssignment(null);
    } catch (error) {
      console.error("Failed to save assignment:", error);
      toast.error("Failed to save assignment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!existingAssignment) return;
    setDeleting(true);
    try {
      await apiService.delete(
        `/api/configurations/components-managers/${existingAssignment.id}/`,
      );
      invalidateCache();
      notifyAssignmentsUpdated();
      toast.success("Assignment removed successfully!");
      setOpenDeleteDialog(false);
      setSelectedComponent("");
      setFormData(emptyForm);
      setExistingAssignment(null);
    } catch (error) {
      console.error("Failed to delete assignment:", error);
      toast.error("Failed to remove assignment. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveNewComponent = async () => {
    if (!newComponentData.name.trim()) {
      toast.error("Component name is required");
      return;
    }

    setSavingNewComponent(true);
    try {
      const response = await apiService.post<Component>(
        "/api/endpoint-request/components/",
        {
          name: newComponentData.name.trim(),
          description: newComponentData.description.trim() || "",
        },
      );
      const saved = response.data;
      toast.success("Component added successfully!");

      setComponents((prev) => [...prev, saved]);
      setSelectedComponent(saved.id.toString());

      setOpenAddComponentDialog(false);
      setNewComponentData({ name: "", description: "" });
    } catch (error) {
      console.error("Failed to save new component:", error);
      toast.error("Failed to add component. Please try again.");
    } finally {
      setSavingNewComponent(false);
    }
  };

  const selectedComponentName =
    components.find((c) => c.id.toString() === selectedComponent)?.name || "";

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Assign Component Lead
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        {/* Component Select */}
        <FormControl fullWidth sx={{ mb: 1 }}>
          <InputLabel>Select Component *</InputLabel>
          <Select
            value={selectedComponent}
            label="Select Component *"
            onChange={(e) => setSelectedComponent(e.target.value)}
            disabled={loadingComponents}
            endAdornment={
              loadingComponents ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : null
            }
          >
            {components.map((c) => (
              <MenuItem key={c.id} value={c.id.toString()}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          startIcon={<AddCircleOutlineOutlined />}
          onClick={() => {
            setOpenAddComponentDialog(true);
            setNewComponentData({ name: "", description: "" });
          }}
          size="small"
          sx={{ mb: 2, textTransform: "none" }}
        >
          Add Component
        </Button>

        {/* Checking assignment spinner */}
        {checkingAssignment && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Checking existing assignment...
            </Typography>
          </Box>
        )}

        {/* Existing assignment banner */}
        {!checkingAssignment && existingAssignment && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This component already has an assignment. You can update it below or
            remove it.
          </Alert>
        )}

        <CustomTextField
          fullWidth
          margin="normal"
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          disabled={!selectedComponent || checkingAssignment}
          multiline
          rows={3}
        />

        <CustomTextField
          fullWidth
          margin="normal"
          label="Version"
          name="version"
          value={formData.version}
          onChange={handleChange}
          disabled={!selectedComponent || checkingAssignment}
        />

        <CustomTextField
          fullWidth
          margin="normal"
          label="WhatsApp Group URL"
          name="whatsapp_group_url"
          value={formData.whatsapp_group_url}
          onChange={handleChange}
          disabled={!selectedComponent || checkingAssignment}
          placeholder="https://chat.whatsapp.com/..."
        />
        <CustomTextField
          fullWidth
          margin="normal"
          label="Figma URL"
          name="figma_url"
          value={formData.figma_url}
          onChange={handleChange}
          disabled={!selectedComponent || checkingAssignment}
          placeholder="https://www.figma.com/..."
        />
        <Autocomplete
          fullWidth
          sx={{ mt: 2 }}
          disabled={!selectedComponent || loadingUsers || checkingAssignment}
          options={users}
          getOptionLabel={(u) => getUserLabel(u)}
          filterOptions={(options, { inputValue }) =>
            inputValue.trim() === ""
              ? []
              : options.filter((u) =>
                  getUserLabel(u)
                    .toLowerCase()
                    .includes(inputValue.toLowerCase()),
                )
          }
          noOptionsText="Type to search for a developer"
          value={
            users.find((u) => u.id.toString() === formData.lead_developer_id) ||
            null
          }
          onChange={(_, newValue) =>
            setFormData((prev) => ({
              ...prev,
              lead_developer_id: newValue ? newValue.id.toString() : "",
            }))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Lead Developer *"
              placeholder="Type to search..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingUsers ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Autocomplete
          fullWidth
          sx={{ mt: 2 }}
          disabled={!selectedComponent || loadingUsers || checkingAssignment}
          options={users}
          getOptionLabel={(u) => getUserLabel(u)}
          filterOptions={(options, { inputValue }) =>
            inputValue.trim() === ""
              ? []
              : options.filter((u) =>
                  getUserLabel(u)
                    .toLowerCase()
                    .includes(inputValue.toLowerCase()),
                )
          }
          noOptionsText="Type to search for a developer"
          value={
            users.find(
              (u) => u.id.toString() === formData.backup_developer_id,
            ) || null
          }
          onChange={(_, newValue) =>
            setFormData((prev) => ({
              ...prev,
              backup_developer_id: newValue ? newValue.id.toString() : "",
            }))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Backup Developer"
              placeholder="Type to search..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingUsers ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {/* Action buttons */}
        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={!selectedComponent || submitting || checkingAssignment}
          >
            {submitting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {existingAssignment ? "Updating..." : "Assigning..."}
              </>
            ) : existingAssignment ? (
              "Update Assignment"
            ) : (
              "Assign"
            )}
          </Button>

          {existingAssignment && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={() => setOpenDeleteDialog(true)}
              disabled={submitting || checkingAssignment}
            >
              Remove Assignment
            </Button>
          )}
        </Box>
      </Box>

      {/* Success modal */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Typography sx={{ py: 2 }}>
            Component assignment saved successfully!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove Assignment</DialogTitle>
        <DialogContent>
          <Typography sx={{ py: 2 }}>
            Are you sure you want to remove the assignment for{" "}
            <strong>{selectedComponentName}</strong>? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Removing...
              </>
            ) : (
              "Remove"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Component dialog */}
      <Dialog
        open={openAddComponentDialog}
        onClose={() => setOpenAddComponentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Component</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Component Name *"
            fullWidth
            value={newComponentData.name}
            onChange={(e) =>
              setNewComponentData((prev) => ({ ...prev, name: e.target.value }))
            }
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Component Description"
            fullWidth
            multiline
            rows={4}
            value={newComponentData.description}
            onChange={(e) =>
              setNewComponentData((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            placeholder="Describe what this component is used for..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenAddComponentDialog(false)}
            disabled={savingNewComponent}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewComponent}
            variant="contained"
            disabled={savingNewComponent || !newComponentData.name.trim()}
          >
            {savingNewComponent ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UpdateFooter;
